import { NavLink } from "react-router-dom";
import {
  Briefcase,
  Key,
  ShieldCheck,
  Plug,
  Settings,
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  Ticket,
  Lock,
  FlagTriangleRight,
} from "lucide-react";
import { Select } from "@/components/ui/select";
import type { OrgMeta } from "@shared/protocol";
import { cn } from "@/lib/utils";

interface SidebarProps {
  org: OrgMeta;
  orgs: OrgMeta[];
  flags: Record<string, boolean>;
  switchOrg: (orgId: string) => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Only ever shown to a super_admin, on a super org — see isSuperAdmin
   *  below. Rendered with a lock icon so it visually reads as gated,
   *  distinct from the regular nav (which every org/role sees). */
  superAdminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/keys", label: "API Keys", icon: Key },
  { to: "/credentials", label: "Credentials", icon: ShieldCheck },
  { to: "/users", label: "Users", icon: Users },
  { to: "/settings", label: "Settings", icon: Settings },
];

const SUPER_ADMIN_NAV: NavItem[] = [
  {
    to: "/admin/orgs",
    label: "Organizations",
    icon: Building2,
    superAdminOnly: true,
  },
  { to: "/admin/roles", label: "Roles", icon: Shield, superAdminOnly: true },
  {
    to: "/admin/codes",
    label: "Access Codes",
    icon: Ticket,
    superAdminOnly: true,
  },
  {
    to: "/admin/flags",
    label: "Feature Flags",
    icon: FlagTriangleRight,
    superAdminOnly: true,
  },
];

export function Sidebar({ org, orgs, flags, switchOrg }: SidebarProps) {
  // Checked across ALL of the user's orgs, not just the active one — a
  // super_admin should see this link regardless of which org they've
  // switched to.
  const isSuperAdmin = orgs.some((o) => o.role === "super_admin");

  // Integrations is a regular (non-admin) page, but gated behind the
  // 'integrations_page' feature flag while there's no GitHub App
  // configured to actually install — see IntegrationsPage.tsx. A
  // super_admin always sees it (to preview/test), with the same lock
  // icon the admin-only pages get, so it reads as gated rather than
  // just another normal nav item when the flag is off.
  const integrationsFlagOn = !!flags.integrations_page;
  const integrations: NavItem[] =
    integrationsFlagOn || isSuperAdmin
      ? [
          {
            to: "/integrations",
            label: "Integrations",
            icon: Plug,
            superAdminOnly: !integrationsFlagOn,
          },
        ]
      : [];

  // NAV is [Dashboard, Jobs, API Keys, Credentials, Users, Settings] —
  // Integrations reinserts after Credentials (index 3), matching where it
  // sat before it moved to this flag-gated slice.
  const nav = [
    ...NAV.slice(0, 4),
    ...integrations,
    ...NAV.slice(4),
    ...(isSuperAdmin ? SUPER_ADMIN_NAV : []),
  ];
  return (
    <aside className="w-60 border-r bg-card flex flex-col">
      <div className="p-4 border-b">
        <div className="font-semibold text-lg">Pure</div>
      </div>
      <div className="p-3 border-b">
        {orgs.length > 1 ? (
          <Select value={org.id} onChange={(e) => switchOrg(e.target.value)}>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        ) : (
          <div className="text-sm px-2 py-1">
            <div className="font-medium truncate">{org.name}</div>
            <div className="text-xs text-muted-foreground">{org.role}</div>
          </div>
        )}
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {nav.map(({ to, label, icon: Icon, superAdminOnly }) => (
          <NavLink
            key={to}
            to={to}
            // Exact match for everything except /jobs, which intentionally
            // stays highlighted for its /jobs/new and /jobs/:id sub-routes
            // (there's no separate nav entry for those) — every other item
            // is now a distinct top-level page with no children in the nav,
            // so a prefix match would wrongly light up e.g. Settings while
            // on /users if it ever shared a path prefix again.
            end={to !== "/jobs"}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-accent text-accent-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
              )
            }
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{label}</span>
            {superAdminOnly && (
              <Lock
                className="h-3 w-3 text-muted-foreground shrink-0"
                aria-label="Super admin only"
              />
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
